export { hslaBaseColors, themeColors } from '@loomhq/lens';

export const toHslString = ({
  h,
  l,
  s,
}: {
  h: number;
  s: number;
  l: number;
}): string => `hsl(${h}, ${s}%, ${l}%)`;

export const toHslaString = ({
  h,
  l,
  s,
  a,
}: {
  h: number;
  s: number;
  l: number;
  a: number;
}): string => `hsla(${h}, ${s}%, ${l}%, ${a})`;

export const toRgbString = ({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): string => `rgb(${r}, ${g}, ${b})`;

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */
export function hslToRgb(
  h: number,
  s: number,
  l: number
): { r: number; g: number; b: number } {
  let r;
  let g;
  let b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = function hue2rgb(p, q, t) {
      if (t < 0) {
        t += 1;
      }

      if (t > 1) {
        t -= 1;
      }

      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }

      if (t < 1 / 2) {
        return q;
      }

      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }

      return p;
    };

    h = h / 255;
    s = s / 100;
    l = l / 100;

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}
